/**
 * useParentHomeData — fetches for the parent home tab.
 *
 * Owns: getSubscribePricing, getStaticData, parent-mode enter log, school assessments,
 * next live sessions (AsParent when email known), the last live report, the pending « garder son
 * tuteur » choices, SEND_TUTOR_MESSAGE form reset. Mapping lives in the view-model.
 */
import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { formIds } from "../../../../../../data/constants/formConstants"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { resetUploadedFiles } from "../../../../../../data/redux/actions/formData"
import { liveReportService } from "../../../../../../data/services/liveReportService"
import { liveService } from "../../../../../../data/services/liveService"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { staticDataService } from "../../../../../../data/services/staticDataService"
import { subscriptionService } from "../../../../../../data/services/subscriptionService"
import { tutorSlotMovesService } from "../../../../../../data/services/tutorSlotMovesService"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

export type UseParentHomeDataArgs = {
    selectedUserId?: string | number | null
    /** formData.user email when identity matches — drives AsParent lives fetch. */
    studentEmail?: string | null
    /** formData.user id when identity matches — drives the last-report fetch. */
    studentId?: string | number | null
    identityReady?: boolean
}

export function useParentHomeData(args: UseParentHomeDataArgs) {
    const { selectedUserId, studentEmail = null, studentId = null, identityReady = false } = args
    const dispatch = useDispatch()

    const displayedStudentId = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => selectDisplayedStudent(state)?.id as string | number | undefined
    )

    const fetchPricing = useCallback(() => {
        dispatch(apiRequest(subscriptionService.getSubscribePricing) as never)
    }, [dispatch])

    const fetchAssessments = useCallback(() => {
        if (
            selectedUserId == null ||
            displayedStudentId == null ||
            String(selectedUserId) !== String(displayedStudentId)
        ) {
            return
        }
        dispatch(
            apiRequest(
                schoolAssessmentsService.getSchoolAssessments,
                {},
                {},
                { student_id: selectedUserId, upcoming: true }
            ) as never
        )
    }, [dispatch, selectedUserId, displayedStudentId])

    const fetchLives = useCallback(() => {
        if (!identityReady) return
        if (studentEmail) {
            dispatch(
                apiRequest(
                    liveService.getAllMyNextLiveSessionsAsParent,
                    {},
                    {},
                    { student_email: studentEmail }
                ) as never
            )
            return
        }
        dispatch(apiRequest(liveService.getAllMyNextLiveSessions, {}, {}, {}) as never)
    }, [dispatch, identityReady, studentEmail])

    /**
     * « Dernier bilan » — page 1 de la liste des rapports de l'élève, l'endpoint qu'utilise déjà
     * l'onglet Rapports. L'accueil ne charge sinon rien de cette famille : `tutor_comment` n'est
     * servi que là.
     *
     * Il DÉPOSE et ne consomme pas. La tranche `getStudentLiveReports` est partagée avec l'onglet
     * Rapports, qui la lit puis l'idle (`useLiveReportsData`) ; deux consommateurs sur la même
     * tranche se voleraient le succès l'un à l'autre. La donnée, elle, arrive par le réducteur
     * (`state.data.liveReports.liveReports`), ce que le view-model lit.
     */
    const fetchLastReport = useCallback(() => {
        if (studentId == null) return
        dispatch(apiRequest(liveReportService.getStudentLiveReports, { id: studentId }, {}, { page: 1 }) as never)
    }, [dispatch, studentId])

    /**
     * Les choix « garder son tuteur » en attente — sous le jeton de l'élève, donc seulement une fois
     * l'identité réconciliée (une chip qui bascule fait DEVENIR le compte connecté cet enfant). Il
     * DÉPOSE : la donnée arrive par le réducteur (`state.data.tutorSlotMoves`).
     */
    const fetchTutorSlotMoves = useCallback(() => {
        if (studentId == null) return
        dispatch(apiRequest(tutorSlotMovesService.getTutorSlotMoves) as never)
    }, [dispatch, studentId])

    const pricingUserRef = useRef<string | number | null | undefined>(undefined)
    const loggedParentModeEnterRef = useRef(false)

    // A child chip calls `switchAccount`: the connected account BECOMES that child
    // (`ParentMainLayout`), so the pricing payload — which fills `formData.user`, and with it
    // `studentEmail` / `identityReady` and therefore the assessments + lives fetches below — has to
    // follow. Same trigger as legacy `ParentSubscribeScreen` (effect deps `[selectedUser,
    // currentUser?.id]`); the ref keeps it to one fetch per identity.
    useEffect(() => {
        if (pricingUserRef.current !== undefined && String(pricingUserRef.current) === String(displayedStudentId))
            return
        pricingUserRef.current = displayedStudentId ?? null
        fetchPricing()
    }, [fetchPricing, displayedStudentId])

    useEffect(() => {
        dispatch(apiRequest(staticDataService.getStaticData) as never)
    }, [dispatch])

    // Legacy logs "enter" once per mount (`[]` effect). `parent_mode_logs` only feeds metrics, so
    // re-logging on every chip switch would inflate them — hence the guard ref, not the id in deps.
    useEffect(() => {
        if (loggedParentModeEnterRef.current || displayedStudentId == null) return
        loggedParentModeEnterRef.current = true
        dispatch(apiRequest(subscriptionService.logParentModeAction, {}, { action: "enter" }) as never)
    }, [dispatch, displayedStudentId])

    useEffect(() => {
        fetchAssessments()
    }, [fetchAssessments])

    useEffect(() => {
        fetchLives()
    }, [fetchLives])

    // `studentId` ne change qu'au montage et à la bascule d'enfant — le réducteur `liveReports`
    // se vide lui-même sur `SWITCH_ACCOUNT`, donc l'accueil ne montrerait rien sans ce rappel.
    useEffect(() => {
        fetchLastReport()
    }, [fetchLastReport])

    useEffect(() => {
        fetchTutorSlotMoves()
    }, [fetchTutorSlotMoves])

    useEffect(() => {
        dispatch(resetUploadedFiles(formIds.SEND_TUTOR_MESSAGE) as never)
    }, [dispatch])

    return {
        refreshPricing: fetchPricing,
        refreshAssessments: fetchAssessments,
        refreshLives: fetchLives,
        refreshLastReport: fetchLastReport,
    }
}
